import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  LatestRate,
  LatestRateSearch,
  RateTableConnectionFormatEnum,
  SearchRateResponseRateTableConnectionArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchRatesTableDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchRates.rateTableConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Rates Table' } });

// Service Function Signature
type ServiceMethodPayload = {
  search: LatestRateSearch[];
  format: RateTableConnectionFormatEnum;
};
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<LatestRate>>;
}

// SearchRatesTable Service
export const { searchRatesTable, refetchRatesTable, useSearchRatesTable } = ((): {
  searchRatesTable: ServiceMethod;
  refetchRatesTable: ServiceMethod;
  useSearchRatesTable: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchRatesTableDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<LatestRate>> => {
        const args: SearchRateResponseRateTableConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: payload?.search,
            format: payload?.format,
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchRatesTable: fetch,
    refetchRatesTable: refetch,
    useSearchRatesTable: useService,
  };
})();
